
import {
  get,
  isPlainObject,
} from "./functions"
import { urlTransform, queryStringAssign } from "./formHelper";

/*
  turboFrameTransform("#<turbo_frame_id>", {
    deleteParams: ["staff_payroll"], // staff_payroll 혹은 staff_payroll[ 로 시작하는 모든 파라미터를 삭제 
    assignParams: {
      staff_payroll_id: id,
      staff_payroll_type: "StaffPayroll",
    }, // staff_payroll_id, staff_payroll_type 파라미터를 추가
    appendParams: {
    } // 파라메터 엔트리 추가
  })
*/
export function turboFrameTransform(turboFrameId, transformParams = {}) {
  const turboFrame = document.querySelector(`#${turboFrameId}`);

  if (!turboFrame) {
    throw new Error(`turboFrameTransform() turboFrame#id="${turboFrameId}" not found`);
  }

  const originalUrl = turboFrame.dataset.src || turboFrame.src;

  if (!originalUrl) {
    throw new Error(`turboFrameTransform() turboFrame#id="${turboFrameId}" src is required`);
  }

  // urlTransform을 사용하여 URL 변환
  const transformedUrl = urlTransform(originalUrl, transformParams);

  // turbo-frame에 새 URL 적용
  turboFrame.dataset.src = transformedUrl;
  turboFrame.src = transformedUrl;

  return transformedUrl;
}

export function turboFrameReload(turboFrameId) {
  const turboFrame = document.querySelector(`#${turboFrameId}`);

  if (!turboFrame) {
    throw new Error(`safeTurboFrameReload() turboFrame#id="${turboFrameId}" not found`);
  }

  // 1. disabled 상태면 disabled를 풀고 reload를 유도함
  if (turboFrame.disabled) {
    turboFrame.disabled = false;
    return;
  }

  // 2. 현재 로드가 '완료되지 않은' 경우(즉, fetch 진행 중), 경고 및 재요청 차단
  if (!turboFrame.complete) {
    console.warn(`safeTurboFrameReload(): turbo-frame#${turboFrameId}은 현재 fetch 중이므로, 중복 요청을 방지합니다.`);
    return;
  }

  if (!turboFrame.src) {
    throw new Error(`safeTurboFrameReload() turboFrame#id="${turboFrameId}" src is required`);
  }

  return turboFrame.reload();
}

// 마치 터보프레임 리로드 하듯 느낌을 주는 목적에 가까워 스크롤은 변경하지 않습니다.
export function turboLocationReload(options = { preserveScroll: true, assignParams: null }) {
  // 스크롤 위치 저장 (기본값: 유지)
  const preserveScroll = options?.preserveScroll !== false

  if (preserveScroll) {
    const scrollPosition = { x: window.scrollX, y: window.scrollY }
    document.addEventListener('turbo:load', () => {
      window.scrollTo(scrollPosition.x, scrollPosition.y)
    }, { once: true })
  }

  if (options?.assignParams) {
    const targetHref = queryStringAssign(window.location.href, options.assignParams)
    Turbo.visit(targetHref, { action: "replace" })
  } else {
    Turbo.visit(window.location.href, { action: "replace" })
  }
}

export async function actionCableHelper({ channel, roomId }, callbackHooks) {
  const destination = { channel: channel }
  if (roomId && typeof roomId === "string") { destination.roomId = roomId }
  let createdSubscription = null
  let handleConnected = () => { console.warn("no handle 'enter'") }
  let handleDisconnected = () => { console.log(`Channel disconnected`, destination) }
  let handleReceived = () => { console.warn("no handle 'data'") }
  function handleUnsubscribe() {
    createdSubscription.unsubscribe()
    handleDisconnected()
    handleDisconnected = () => { console.log(`Channel disconnected`, destination) }
    handleReceived = () => { console.warn(`Closed subscriptions but recive data`, data, destination) }
  }
  const controller = Object.defineProperties({}, {
    channel: {
      enumerable: true,
      configurable: false,
      writable: false,
      value: channel
    },
    unsubscribe: {
      enumerable: false,
      configurable: false,
      writable: false,
      value: handleUnsubscribe
    },
    disconnect: {
      enumerable: false,
      configurable: false,
      writable: false,
      value: handleUnsubscribe
    },
    exit: {
      enumerable: false,
      configurable: false,
      writable: false,
      value: handleUnsubscribe
    }
  })

  await import("../channels/consumer").then((consumerModule) => {
    const consumer = consumerModule.default

    createdSubscription = consumer.subscriptions.create(destination, {
      connected(...args) {
        handleConnected(...args)
      },
      disconnected(...args) {
        console.log('diconnected')
        handleDisconnected(...args)
      },
      received(data) {
        handleReceived(data)
      },
    })
    const eventHandles = callbackHooks(controller)
    handleConnected = eventHandles?.enter ?? handleConnected
    handleDisconnected = eventHandles?.exit ?? handleDisconnected
    handleReceived = eventHandles?.data ?? handleReceived
  })
  return controller
}

export function formToFetchConfig(form, submitter) {
  const formData = new FormData(form, submitter)
  // for (const pair of formData.entries()) {
  //   console.log(`${pair[0]}: ${pair[1]}`);
  // }
  return {
    url: form.getAttribute("action"),
    method: form.method.toUpperCase(),
    body: formData,
  }
}

function fetchPropsParser(option = {}, optionalOption) {
  // 다형성 지원
  if (typeof option == "string") {
    option = Object.assign({ url: option }, optionalOption)
  }

  // url
  if (!option["url"]) {
    const { origin, pathname } = location
    option["url"] = `${origin}${pathname}`
  }

  if (option["query"]) {
    const queriedUrl = option["url"]
    const assignedUrl = queryStringAssign(queriedUrl, option["query"])
    option["url"] = assignedUrl
  }

  // headers
  if (!option["headers"]) {
    option["headers"] = {}
  }

  // body가 객체일 경우 json으로 변환
  if (isPlainObject(option.body)) {
    option.body = JSON.stringify(option.body);
  }

  const { url, useCatchResponse = false, ...fetchOptions } = option
  return [url, fetchOptions, { useCatchResponse }]
}

function getMetaCSRFToken() {
  const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
  if (csrfToken) {
    return csrfToken;
  } else {
    console.warn(`jsonFetch() meta[name="csrf-token"] 토큰을 찾을 수 없습니다.`)
    return null;
  }
}

// json 형태로 turbo 를 자동으로 처리하지 않는 요청
export function jsonFetch(option = {}, optionalOption) {
  const [url, fetchOptions, { useCatchResponse }] = fetchPropsParser(option, optionalOption)

  // method가 명시되지 않았다면 post가 우선 
  if (!fetchOptions["method"]) {
    fetchOptions["method"] = "POST";
  }

  // Content-Type이 명시되지 않았다면 json이 우선 
  if (fetchOptions.body instanceof FormData) {
    // FormData일 경우 Content-Type을 삭제 (브라우저가 자동으로 설정)
    if (fetchOptions["headers"]["Content-Type"]) {
      delete fetchOptions["headers"]["Content-Type"];
    }
  } else if (!fetchOptions["headers"]["Content-Type"]) {
    fetchOptions["headers"]["Content-Type"] = "application/json";
  }

  // Content-Type이 명시되지 않았다면 json이 우선 
  if (!fetchOptions["headers"]["Accept"]) {
    fetchOptions["headers"]["Accept"] = "application/json";
  }


  // csrf 토큰 추가
  if (!fetchOptions["headers"]["X-CSRF-Token"]) {
    fetchOptions["headers"]["X-CSRF-Token"] = getMetaCSRFToken();
  }

  let fetchWorker = fetch(url, fetchOptions)
  if (useCatchResponse) {
    fetchWorker = fetchWorker.then(useCatchResponseError)
  }
  return fetchWorker
}

// html 형태로 turbo 를 자동으로 처리하지 않는 요청
export function htmlFetch(option = {}, optionalOption) {
  const [url, fetchOptions, { useCatchResponse }] = fetchPropsParser(option, optionalOption)

  // method가 명시되지 않았다면 get이 우선 
  if (!fetchOptions["method"]) {
    fetchOptions["method"] = "GET";
  }

  // Content-Type이 명시되지 않았다면 json이 우선 
  if (fetchOptions.body instanceof FormData) {
    // FormData일 경우 Content-Type을 삭제 (브라우저가 자동으로 설정)
    if (fetchOptions["headers"]["Content-Type"]) {
      delete fetchOptions["headers"]["Content-Type"];
    }
  } else if (!fetchOptions["headers"]["Content-Type"]) {
    fetchOptions["headers"]["Content-Type"] = "application/json";
  }


  // Content-Type이 명시되지 않았다면 html이 우선 
  if (!fetchOptions["headers"]["Accept"]) {
    fetchOptions["headers"]["Accept"] = "text/html";
  }

  // csrf 토큰 추가
  if (!fetchOptions["headers"]["X-CSRF-Token"]) {
    fetchOptions["headers"]["X-CSRF-Token"] = getMetaCSRFToken();
  }

  let fetchWorker = fetch(url, fetchOptions)
  if (useCatchResponse) {
    fetchWorker = fetchWorker.then(useCatchResponseError)
  }
  return fetchWorker
}

// fetch 후 랜더링을 원하는 경우
/*
  fetch 유의사항
  Turbo.fetch는 실제로 다음 헤더를 포함함
  'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
  'X-Requested-With': 'XMLHttpRequest',
  'X-Turbo-Referrer': window.location.href

  Turbo.fetch 이후 처리는 다음과 후처리를 직접 수행시켜야 함
*/

export function turboRequest(option = {}, optionalOption) {
  const [url, fetchOptions, { useCatchResponse }] = fetchPropsParser(option, optionalOption)

  // method가 명시되지 않았다면 get이 우선 
  if (!fetchOptions["method"]) {
    fetchOptions["method"] = "GET";
  }

  if (fetchOptions.body instanceof FormData) {
    // FormData일 경우 Content-Type을 삭제 (브라우저가 자동으로 설정)
    if (fetchOptions["headers"]["Content-Type"]) {
      delete fetchOptions["headers"]["Content-Type"];
    }
  } else if (!fetchOptions["headers"]["Content-Type"]) {
    fetchOptions["headers"]["Content-Type"] = "application/json";
  }

  if (!fetchOptions["headers"]["Accept"]) {
    fetchOptions["headers"]["Accept"] = "text/vnd.turbo-stream.html, text/html, application/xhtml+xml";
  }

  if (!fetchOptions["headers"]["X-CSRF-Token"]) {
    fetchOptions["headers"]["X-CSRF-Token"] = getMetaCSRFToken();
  }

  let fetchWorker = Turbo.fetch(url, fetchOptions)

  if (useCatchResponse) {
    fetchWorker = fetchWorker.then(useCatchResponseError)
  }

  fetchWorker = fetchWorker.then(response => {
    // redirect 체크
    if (response.redirected) {
      return Turbo.visit(response.url)
    }

    // 또는 특정 상태 코드 체크
    if (response.status === 302 || response.status === 301) {
      const location = response.headers.get('Location')
      return Turbo.visit(location)
    }

    // Turbo Stream 처리
    if (response.headers.get('content-type')?.includes('turbo-stream')) {
      return response.text().then(html => Turbo.renderStreamMessage(html))
    }
  })
  return fetchWorker
}

// fetch 요청에서 정상 응답 범위를 넘으면 프로미스 체인에서 catch 로 처리하도록 도와주는 함수
export async function useCatchResponseError(response) {
  if (!response.ok) {
    let errorText = "";
    await response.text().then((text) => {
      const trimmedText = text.trim()

      // json 형태일 경우 json 파싱 시도
      if (trimmedText.startsWith("{")) {
        try {
          const json = JSON.parse(trimmedText)
          errorText = json.message || json.error || trimmedText
        } catch {
          errorText = trimmedText
        }
      } else if (trimmedText.length > 0) {
        errorText = trimmedText
      } else {
        throw new Error(`Unexpected response: errorText is empty`);
      }
    }).catch((error) => {
      console.warn(error.message)
      errorText = `Server response: ${response.status} ${response.statusText}`
    })
    const newError = new Error(errorText);
    newError.response = response;
    // 호출 지점의 스택을 따로 수집
    const callerStack = newError.stack
      .split('\n')
      .slice(2)
      .join('\n');
    newError.stack = `${newError.name}: ${newError.message}\n${callerStack}`;
    throw newError;
  }
  // 정상 응답일 경우 다음 then으로 응답 객체를 반환
  return response;
}

// 스트림을 줄 단위로 읽을 수 있는 리더 생성
// 이 스트림 함수는 최대한 각 훅별로 스레드를 양보하도록 설계되었습니다.
// 사용법:
// const reader = readableTextHelper(response.body, {
//   enter: ()=> { ... },
//   line: (line, index)=> { ... },
//   lines: (lines, startIndex)=> { ... },
//   exit: ()=> { ... },
// });
//
// data 옵션으로 라인 변환 (transform):
// - data: "sse" → SSE 형식 파싱 (data: 접두사 제거, 빈줄 필터링)
// - data: (line) => transformedLine → 커스텀 변환, null 반환 시 해당 라인 무시
//
// index는 transform 후 유효한 라인들만 카운트됨
// - line: (line, index) → 0, 1, 2, 3... 순차 증가
// - lines: (lines, startIndex) → 해당 배치의 시작 index
//
// SSE 모드 예시:
// readableTextHelper(response.body, {
//   data: "sse",
//   line: (jsonStr, index) => {
//     if (jsonStr === "[DONE]") return;
//     const parsed = JSON.parse(jsonStr);
//     console.log(`[${index}]`, parsed.choices[0].delta.content);
//   },
//   exit: () => console.log("완료"),
// });
export function readableTextHelper(readableStream, option) {
  const reader = readableStream.getReader();
  const decoder = new TextDecoder('utf-8');

  // option이 함수인 경우 호출하여 hooks 객체로 변환
  const hooks = typeof option === 'function' ? option() : option;

  // data transform 함수 설정
  let dataTransform = null;
  if (hooks?.data === 'sse') {
    // SSE 모드: "data: " 접두사 제거, 빈 줄 필터링
    dataTransform = (line) => {
      const trimmed = line.trim();
      if (trimmed === '') return null; // 빈 줄 무시
      if (trimmed.startsWith('data: ')) return trimmed.slice(6);
      if (trimmed.startsWith('data:')) return trimmed.slice(5);
      return trimmed;
    };
  } else if (typeof hooks?.data === 'function') {
    dataTransform = hooks.data;
  }

  // 상태 변수
  let finished = false;
  let lineBuffer = '';
  let lineIndex = 0;

  function processChunk(chunk) {
    lineBuffer += chunk;

    // 줄바꿈으로 분리하여 완성된 라인 처리
    const rawLines = lineBuffer.split('\n');

    // 마지막 요소는 미완성 라인일 수 있으므로 버퍼에 보관
    lineBuffer = rawLines.pop() || '';

    // transform 적용 및 null 필터링
    let lines = rawLines;
    if (dataTransform) {
      lines = rawLines
        .map(dataTransform)
        .filter((line) => line !== null);
    }

    // 완성된 라인들을 콜백으로 전달
    if (lines.length > 0) {
      const startIndex = lineIndex;
      hooks?.lines?.(lines, startIndex);
      if (typeof hooks?.line === 'function') {
        for (const line of lines) {
          hooks.line(line, lineIndex);
          lineIndex++;
        }
      } else {
        lineIndex += lines.length;
      }
    }
  }

  function processEnd() {
    // 버퍼에 남은 데이터가 있으면 마지막 라인으로 처리
    if (lineBuffer.length > 0) {
      const transformed = dataTransform ? dataTransform(lineBuffer) : lineBuffer;
      if (transformed !== null) {
        hooks?.line?.(transformed, lineIndex);
        hooks?.lines?.([transformed], lineIndex);
        lineIndex++;
      }
      lineBuffer = '';
    }
    finished = true;
    hooks?.exit?.();
  }

  function readNext() {
    reader.read().then(({ done, value }) => {
      if (done) {
        processEnd();
        return;
      }

      // 바이트 데이터를 문자열로 디코딩
      const chunk = decoder.decode(value, { stream: true });
      processChunk(chunk);

      // 다음 청크를 비동기로 읽기 (자연스럽게 이벤트 루프에 제어권 양보)
      readNext();
    });
  }

  // 읽기 시작
  if (hooks?.enter) {
    Promise
      .resolve(hooks?.enter())
      .then(() => {
        readNext()
      })
  } else {
    readNext()
  }

  return {
    get finished() { return finished; }
  }
}
